import { computed, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import type { DashboardValue } from './chart/types'

/**
 * Konvertiert einen Semester-String in einen URL-Slug.
 * Entfernt Leerzeichen und Schrägstriche und macht alles klein.
 *
 * @example "SoSe 2026" → "sose2026"
 * @example "WiSe 2024/25" → "wise202425"
 */
export function toSemesterSlug(semester: string): string {
    return semester.toLowerCase().replace(/[\s/]/g, '')
}

/**
 * Konvertiert einen Modulnamen in einen URL-Slug.
 * Umlaute werden umgeschrieben, Sonderzeichen durch Bindestriche ersetzt.
 *
 * @example "Mathematik 1" → "mathematik-1"
 */
export function toModulSlug(modulname: string): string {
    return modulname
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

/*
|--------------------------------------------------------------------------
| Reine Hilfsfunktionen (exportiert für direkten Gebrauch ohne Reaktivität)
|--------------------------------------------------------------------------
*/

/**
 * Bestimmt das aktuellste Semester aus einem Datensatz.
 * Fällt auf das aktuelle Kalender-Semester zurück wenn keine Daten vorhanden sind.
 */
export function pickCurrentSemester(data: DashboardValue[]): string {
    if (!data.length) {
        return computeCurrentSemesterFromDate(new Date())
    }
    // year ist YYYYS-kodiert (20251 = SoSe 2025, 20252 = WiSe 2025/26, …)
    // Ein Max über year liefert daher den jüngsten Datensatz.
    const latest = data.reduce((max, row) => (row.year > max.year ? row : max), data[0])
    return latest.semester
}

/** Berechnet das aktuelle Kalender-Semester aus einem Datum. */
export function computeCurrentSemesterFromDate(now: Date): string {
    const month = now.getMonth() + 1
    const year = now.getFullYear()
    if (month >= 4 && month <= 9) {
        return `SoSe ${year}`
    }
    const wiseStartYear = month >= 10 ? year : year - 1
    const nextYearShort = String((wiseStartYear + 1) % 100).padStart(2, '0')
    return `WiSe ${wiseStartYear}/${nextYearShort}`
}

/**
 * Erzeugt eine absteigend geordnete Liste von `count` Semestern,
 * beginnend beim übergebenen `currentSemester`.
 */
export function buildSemesters(currentSemester: string, count: number): string[] {
    const match = currentSemester.match(/^(SoSe|WiSe)\s(\d{4})(?:\/(\d{2}))?$/)

    if (!match) {
        throw new Error(`Ungültiges Semesterformat: "${currentSemester}"`)
    }

    let type = match[1]
    let year = parseInt(match[2], 10)

    const semesters: string[] = []

    for (let i = 0; i < count; i++) {
        if (type === 'SoSe') {
            semesters.push(`SoSe ${year}`)
            type = 'WiSe'
            year = year - 1
        } else {
            const nextYearShort = String((year + 1) % 100).padStart(2, '0')
            semesters.push(`WiSe ${year}/${nextYearShort}`)
            type = 'SoSe'
        }
    }

    return semesters
}

/**
 * Berechnet reaktiv die anzuzeigenden Semester für ein Dashboard-Chart.
 *
 * Akzeptiert als Parameter einen `Ref`, eine Getter-Funktion oder einen
 * Direktwert (`MaybeRefOrGetter`)
 * `toValue()` normalisiert alle drei Formen intern.
 *
 * @example mit toRef (empfohlen für Props):
 * ```ts
 * const { dashboardDataYears } = useSemester(
 *     toRef(props, 'dashboardData'),
 *     toRef(props, 'yearsToDisplay')
 * )
 * ```
 * @example mit Getter-Funktion:
 * ```ts
 * const { dashboardDataYears } = useSemester(
 *     () => props.dashboardData,
 *     () => props.yearsToDisplay
 * )
 * ```
 */
export function useSemester(
    dashboardData: MaybeRefOrGetter<DashboardValue[]>,
    yearsToDisplay: MaybeRefOrGetter<number>
) {
    const dashboardDataYears = computed(() => {
        return buildSemesters(pickCurrentSemester(toValue(dashboardData)), toValue(yearsToDisplay)).reverse()
    })

    return { dashboardDataYears }
}
